import { Link } from "react-router-dom";
import { ArrowRight, Cpu, ChevronDown, MessageCircle, Crown } from "lucide-react";
import MatrixRain from "@/components/MatrixRain";
import DigitalTwin from "@/components/DigitalTwin";
import EscouadeHud from "@/components/EscouadeHud";
import AgentMirror from "@/components/AgentMirror";
import { useI18n } from "@/contexts/I18nContext";
import { buildWhatsAppLink } from "@/lib/contact";

export default function Vision() {
  const { t, lang } = useI18n();

  const scrollToEscouade = () => {
    const el = document.getElementById("escouade");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <>
      <section data-testid="vision-page" className="dncm-stage dncm-vignette min-h-[100vh] flex items-center">
        <MatrixRain density={0.9} speed={1} opacity={1} />

        <div className="dncm-content max-w-7xl mx-auto px-6 lg:px-10 pt-32 pb-24 w-full">
          <div className="grid lg:grid-cols-[1.2fr_1fr] gap-10 items-center">
            <div className="max-w-2xl">
              <div className="flex items-center gap-3 mb-8 dncm-fadein">
                <span className="dncm-pulse-dot" />
                <span className="font-mono-d text-[11px] uppercase tracking-[0.32em] text-white/70">
                  {t("hero_eyebrow")}
                </span>
              </div>

              <h1 data-testid="vision-title" className="font-brand dncm-fadein dncm-delay-1 text-[44px] sm:text-6xl lg:text-7xl leading-[1.02] text-white">
                DNCM
                <br />
                <span className="dncm-gradient-text dncm-cyan-glow">VISION</span>
              </h1>

              <p data-testid="vision-subtitle" className="mt-8 max-w-xl text-white/75 text-lg sm:text-xl font-display dncm-fadein dncm-delay-2">
                {t("hero_subtitle")}
              </p>

              <p data-testid="vision-tagline" className="mt-3 max-w-xl text-[color:var(--dncm-green)] text-base sm:text-lg font-display dncm-fadein dncm-delay-2 dncm-green-glow">
                {t("hero_tagline")}
              </p>

              <div className="mt-10 flex flex-wrap items-center gap-4 dncm-fadein dncm-delay-3">
                <button
                  onClick={scrollToEscouade}
                  data-testid="cta-voir-escouade"
                  className="group inline-flex items-center gap-3 px-6 py-4 rounded-full font-mono-d text-[12px] uppercase tracking-[0.24em] text-[#001018] dncm-btn-glow"
                  style={{ background: "linear-gradient(180deg, #6ff3ff 0%, #00cfff 60%, #00a6cf 100%)" }}
                >
                  <ChevronDown className="w-4 h-4" />
                  {lang === "en" ? "Discover the Squad" : "Découvrir l'Escouade"}
                  <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                </button>

                <Link
                  to="/cockpit"
                  data-testid="cta-cockpit"
                  className="group inline-flex items-center gap-3 px-6 py-4 rounded-full font-mono-d text-[12px] uppercase tracking-[0.24em] text-white border transition-colors"
                  style={{ borderColor: "rgba(0,207,255,0.5)", background: "rgba(0,207,255,0.04)" }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = "rgba(0,207,255,0.12)")}
                  onMouseLeave={(e) => (e.currentTarget.style.background = "rgba(0,207,255,0.04)")}
                >
                  <Cpu className="w-4 h-4" />
                  {t("cta_cockpit")}
                  <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                </Link>

                <a
                  data-testid="cta-whatsapp"
                  href={buildWhatsAppLink()}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-3 px-6 py-4 rounded-full font-mono-d text-[12px] uppercase tracking-[0.24em] text-white/90"
                  style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.12)" }}
                >
                  <MessageCircle className="w-4 h-4 text-[color:var(--dncm-cyan)]" />
                  {t("cta_whatsapp")}
                </a>
              </div>

              <div className="mt-10 inline-flex items-center gap-2 text-white/45 font-mono-d text-[10px] uppercase tracking-[0.24em] dncm-fadein dncm-delay-5">
                <Crown className="w-3.5 h-3.5 text-[color:var(--dncm-green)]" />
                {lang === "en" ? "Private system — you own it" : "Système privé — vous en êtes le propriétaire"}
              </div>
            </div>

            <div className="dncm-fadein dncm-delay-4">
              <DigitalTwin />
            </div>
          </div>
        </div>

        <HudCorner pos="tl" />
        <HudCorner pos="tr" />
        <HudCorner pos="bl" />
        <HudCorner pos="br" />
      </section>

      {/* Full Squad as HUD grid + Selena Matrix interactivity */}
      <EscouadeHud />

      {/* Agent Mirror right after the squad */}
      <AgentMirror />
    </>
  );
}

function HudCorner({ pos }) {
  const map = { tl: "top-20 left-6", tr: "top-20 right-6", bl: "bottom-6 left-6", br: "bottom-6 right-6" };
  return (
    <div aria-hidden="true" className={`pointer-events-none hidden md:block absolute ${map[pos]} z-10`}>
      <div
        className="w-8 h-8"
        style={{
          borderColor: "rgba(0,207,255,0.6)",
          borderTopWidth: pos.startsWith("t") ? 1 : 0,
          borderBottomWidth: pos.startsWith("b") ? 1 : 0,
          borderLeftWidth: pos.endsWith("l") ? 1 : 0,
          borderRightWidth: pos.endsWith("r") ? 1 : 0,
          borderStyle: "solid",
        }}
      />
    </div>
  );
}
